<template>
  <div class="mt-2">
    <WarningDisable permission="chat_history" />
    <div class="grow">
      <div class="mb-4 flex items-center justify-between">
        <div class="text-base-content/80 text-xl font-bold">Lịch sử Chat</div>
        <div class="flex gap-2">
          <select
            v-model="selectedType"
            class="select select-bordered select-sm w-full max-w-md font-bold"
          >
            <option
              v-for="(label, value) in typeOptions"
              :key="value"
              :value="value"
            >
              {{ label }}
            </option>
          </select>
        </div>
      </div>

      <Loading v-if="isLoading" />

      <template v-else-if="totalFiltered > 0">
        <div
          class="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8"
        >
          <ChatHistoryCard
            v-for="(chat, index) in chatHistories"
            :key="chat.id"
            :type="chat.type"
            :created_at="chat.created"
            :path="chat.path"
            @click="openSlider(index)"
            class="cursor-pointer transition-transform active:scale-95"
          />
        </div>

        <div v-if="isFetchingNextPage" class="flex justify-center py-4">
          <span class="loading loading-spinner loading-lg text-primary"></span>
        </div>
        <div v-else-if="hasNextPage" class="flex justify-center py-4">
          <button
            @click="() => fetchNextPage()"
            class="btn btn-primary btn-outline btn-wide"
          >
            Tải thêm
          </button>
        </div>
      </template>

      <NotFound v-else />
    </div>
  </div>

  <ImageSlider
    ref="imgSlider"
    :images="sliderImages"
    :initial-index="currentImageIndex"
    :total-length="totalFiltered"
    @near-end="handleNearEnd"
  />
</template>

<script setup lang="ts">
import type { ChatHistoryType } from "@/api/chatHistory";
import ImageSlider, { type SliderImage } from "@/components/ImageSlider.vue";
import Loading from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import { useChatHistoryInfiniteQuery } from "@/composables/useChatHistory";
import { router } from "@/router/router.ts";
import { useInfiniteScroll } from "@vueuse/core";
import dayjs from "dayjs";
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import WarningDisable from "../WarningDisable.vue";
import ChatHistoryCard from "./ChatHistoryCard.vue";

const route = useRoute();

const typeOptions: Record<ChatHistoryType, string> = {
  "zalo-web": "Zalo Web",
  "zalo-app": "Zalo Desktop",
  "telegram-web": "Telegram Web",
  "telegram-app": "Telegram Desktop",
  "what-web": "WhatsApp Web",
  "what-app": "WhatsApp Desktop",
  "viber-app": "Viber Desktop",
  "teams-web": "Teams Web",
  "teams-app": "Teams Desktop",
};

const selectedType = computed({
  get: () => route.params.chat_type,
  set: (newType) => {
    router.replace({
      name: "ClientChatHistory",
      params: { chat_type: newType },
    });
  },
});

const filters = reactive({
  agent_id: route.params.client_id as string,
  // this arrow function ensures `type` receives updates whenever `selectedType` changes
  type: () => selectedType.value as ChatHistoryType,
  size: 24,
});

const currentImageIndex = ref(0);
const imgSlider = ref<InstanceType<typeof ImageSlider> | null>(null);

const {
  chatHistories,
  totalFiltered,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
} = useChatHistoryInfiniteQuery(filters);

onMounted(() => {
  const scrollContainer = document.querySelector("main");
  if (scrollContainer) {
    useInfiniteScroll(
      scrollContainer,
      () => {
        if (hasNextPage.value && !isFetchingNextPage.value) {
          fetchNextPage();
        }
      },
      { distance: 100 },
    );
  }
});

// Convert chat histories to slider images format
const sliderImages = computed<SliderImage[]>(() => {
  return chatHistories.value.map((chat) => ({
    url: `/api/v1/view/chat/${chat.path}`,
    alt: chat.path,
    title: `${typeOptions[chat.type]} - ${dayjs(chat.created).format("DD/MM/YYYY HH:mm:ss")}`,
  }));
});

const openSlider = (index: number) => {
  currentImageIndex.value = index;
  imgSlider.value?.openModal();
};

const handleNearEnd = () => {
  if (hasNextPage.value && !isFetchingNextPage.value) {
    fetchNextPage();
  }
};
</script>
